import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../firebase';
import {
  FILES_KIND_FILE,
  FILES_KIND_FOLDER,
  FILES_OWNER_SCOPE_COMPANY,
  FILES_OWNER_SCOPE_USER,
  FILES_ROOT_PARENT_ID,
  FILES_SOURCE_IMAGE_EDITOR,
  assertFilesStoragePathMatchesOwnerScope,
  buildFilesBlobStoragePath,
  sanitizeFilesNodeName,
  sanitizeFilesStorageSegment,
  storagePathMatchesOwnerScope,
} from './companyFiles.js';
 
import {
  FILES_IMAGE_EDITOR_FOLDER_ID,
  ensureImageEditorLibraryFolders,
  listImageEditorDraftsForOpen,
  toImageEditorDraftOpenItem,
} from './imageEditorDraftsFiles.js';
import { compositeEditorDocument } from './imageEditorDocument.js';
import {
  IMAGE_EDITOR_PROJECT_FORMAT,
  IMAGE_EDITOR_PROJECT_KIND,
  IMAGE_EDITOR_PROJECT_MIME,
  buildLayeredProjectManifest,
  projectLayerFileName,
  projectPreviewFileName,
  rehydrateLayeredProjectManifest,
} from './imageEditorProjectFormat.js';
import { isEditorTextLayer } from './imageEditorTextLayer.js';
import { loadImageSrcToImageData } from './imageEditorAi.js';

const MY_IE_FOLDER_PREFIX = 'folder_my_ie_';
const MY_IE_AUTOSAVE_PREFIX = 'folder_my_ie_autosave_';

/**
 * @param {string} uid
 */
function safeUidSegment(uid) {
  return sanitizeFilesStorageSegment(uid || 'user').slice(0, 40);
}

/**
 * @param {string} companyId
 * @param {string} nodeId
 */
function filesNodeRef(companyId, nodeId) {
  return doc(db, 'companies', companyId, 'files_nodes', nodeId);
}

/**
 * @param {string} companyId
 * @param {string} uid
 */
export function myImageEditorFolderIds(companyId, uid) {
  void companyId;
  const seg = safeUidSegment(uid);
  return {
    myImageEditorFolderId: `${MY_IE_FOLDER_PREFIX}${seg}`,
    myImageEditorAutosaveFolderId: `${MY_IE_AUTOSAVE_PREFIX}${seg}`,
  };
}

/**
 * Ensure My files → Image Editor → Autosave folders for this user.
 * @param {string} companyId
 * @param {string} uid
 */
export async function ensureMyImageEditorFolders(companyId, uid) {
  const cid = String(companyId || '').trim();
  const userId = String(uid || '').trim();
  if (!cid || !userId) throw new Error('companyId and uid are required.');
  const { myImageEditorFolderId, myImageEditorAutosaveFolderId } =
    myImageEditorFolderIds(cid, userId);
  const folderRef = filesNodeRef(cid, myImageEditorFolderId);
  const autosaveRef = filesNodeRef(cid, myImageEditorAutosaveFolderId);

  const ensure = async (targetRef, payload) => {
    const snap = await getDoc(targetRef);
    if (snap.exists()) return;
    await setDoc(targetRef, {
      kind: FILES_KIND_FOLDER,
      ownerScope: FILES_OWNER_SCOPE_USER,
      ownerUid: userId,
      storagePath: null,
      mimeType: null,
      bytes: null,
      source: null,
      linkedProjectId: null,
      linkedAssetId: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdByUid: userId,
      ...payload,
    });
  };

  await ensure(folderRef, {
    parentId: FILES_ROOT_PARENT_ID,
    name: 'Image Editor',
  });
  await ensure(autosaveRef, {
    parentId: myImageEditorFolderId,
    name: 'Autosave',
  });
  return { myImageEditorFolderId, myImageEditorAutosaveFolderId };
}

/**
 * @param {ImageData} imageData
 * @returns {Promise<Blob>}
 */
async function imageDataToPngBlob(imageData) {
  if (typeof document === 'undefined') {
    throw new Error('PNG export requires browser canvas.');
  }
  const canvas = document.createElement('canvas');
  canvas.width = imageData.width;
  canvas.height = imageData.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas context.');
  ctx.putImageData(imageData, 0, 0);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) reject(new Error('Failed to encode PNG.'));
      else resolve(blob);
    }, 'image/png');
  });
}

/**
 * @param {string} documentId
 * @param {string|null|undefined} requestedNodeId
 * @returns {string}
 */
function resolveLayeredAutosaveNodeId(documentId, requestedNodeId) {
  const stable = `autosave_${sanitizeFilesStorageSegment(documentId)}`;
  const req = String(requestedNodeId || '').trim();
  // Never reuse explicit company project node ids (ieproj_* timestamps, etc.).
  if (req.startsWith('autosave_')) return req;
  return stable;
}

/**
 * @param {{
 *   companyId: string,
 *   ownerScope: 'company'|'user',
 *   ownerUid?: string|null,
 *   fileName: string,
 *   blob: Blob,
 * }} params
 */
async function uploadProjectBlob(params) {
  const cid = String(params.companyId || '').trim();
  const fileName = String(params.fileName || '').trim();
  if (!cid || !fileName) throw new Error('companyId and fileName are required.');
  const storagePath = buildFilesBlobStoragePath({
    companyId: cid,
    ownerScope: params.ownerScope,
    ownerUid: params.ownerUid,
    fileName,
  });
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, params.blob, { contentType: 'image/png' });
  const downloadUrl = await getDownloadURL(storageRef);
  return { storagePath, downloadUrl, bytes: params.blob.size || null };
}

/**
 * Save layered editor document into Files node + per-layer PNG blobs.
 *
 * @param {{
 *   companyId: string,
 *   uid: string,
 *   doc: import('./imageEditorDocument').EditorDocument,
 *   fileStem?: string,
 *   nodeId?: string|null,
 *   autosave?: boolean,
 *   ownerScope?: 'company'|'user',
 * }} params
 */
export async function saveLayeredEditorProject(params) {
  const companyId = String(params.companyId || '').trim();
  const uid = String(params.uid || '').trim();
  const editorDoc = params.doc;
  if (!companyId || !uid || !editorDoc) {
    throw new Error('companyId, uid, and doc are required.');
  }
  const autosave = Boolean(params.autosave);
  const ownerScope = autosave
    ? FILES_OWNER_SCOPE_USER
    : params.ownerScope || FILES_OWNER_SCOPE_COMPANY;

  let parentId = FILES_IMAGE_EDITOR_FOLDER_ID;
  let ownerUid = null;
  if (ownerScope === FILES_OWNER_SCOPE_USER) {
    const { myImageEditorAutosaveFolderId, myImageEditorFolderId } =
      await ensureMyImageEditorFolders(companyId, uid);
    parentId = autosave ? myImageEditorAutosaveFolderId : myImageEditorFolderId;
    ownerUid = uid;
  } else {
    await ensureImageEditorLibraryFolders(companyId, uid);
  }

  const stem = sanitizeFilesStorageSegment(
    params.fileStem || `project_${editorDoc.id}`,
  );
  const nodeId = autosave
    ? resolveLayeredAutosaveNodeId(editorDoc.id, params.nodeId)
    : String(params.nodeId || '').trim() || `ieproj_${stem}_${Date.now()}`;

  /** @type {Record<string, string>} */
  const layerStoragePathsById = {};
  const composite = compositeEditorDocument(editorDoc);
  const previewBlob = await imageDataToPngBlob(composite);
  const previewFileName = projectPreviewFileName(editorDoc.id);
  const previewUpload = await uploadProjectBlob({
    companyId,
    ownerScope,
    ownerUid,
    fileName: previewFileName,
    blob: previewBlob,
  });

  /** @type {Array<{ id: string, storagePath: string, bytes: number|null }>} */
  const layerUploads = [];
  for (const layer of editorDoc.layers) {
    // Manifest v2: text layers live in JSON only — no per-layer PNG.
    // Preview composite (above) still bakes glyphs for Files thumbnails.
    if (isEditorTextLayer(layer)) {
      continue;
    }
    const layerBlob = await imageDataToPngBlob(layer.pixels);
    const layerFileName = projectLayerFileName(editorDoc.id, layer.id);
    const uploaded = await uploadProjectBlob({
      companyId,
      ownerScope,
      ownerUid,
      fileName: layerFileName,
      blob: layerBlob,
    });
    layerStoragePathsById[layer.id] = uploaded.storagePath;
    layerUploads.push({
      id: layer.id,
      storagePath: uploaded.storagePath,
      bytes: uploaded.bytes,
    });
  }

  const manifest = buildLayeredProjectManifest(editorDoc, {
    revision: editorDoc.revision,
    persistedRevision: editorDoc.persistedRevision,
    autosave,
    layerStoragePathsById,
    previewStoragePath: previewUpload.storagePath,
  });

  const validatePath = (storagePath, label) => {
    assertFilesStoragePathMatchesOwnerScope({
      companyId,
      ownerScope,
      ownerUid,
      storagePath,
      label,
    });
  };
  validatePath(previewUpload.storagePath, 'Preview');
  for (const layer of layerUploads) {
    validatePath(layer.storagePath, `Layer ${layer.id}`);
  }
  for (const layer of manifest.layers) {
    if (layer?.storagePath) validatePath(layer.storagePath, `Manifest layer ${layer.id}`);
  }

  const fileNameBase = sanitizeFilesNodeName(
    params.fileStem || (autosave ? 'Autosave' : `Project ${editorDoc.id}`),
  );

  const nodeRef = filesNodeRef(companyId, nodeId);
  const prevSnap = await getDoc(nodeRef);
  const basePayload = {
    kind: FILES_KIND_FILE,
    parentId,
    name: fileNameBase,
    ownerScope,
    ownerUid,
    source: FILES_SOURCE_IMAGE_EDITOR,
    linkedProjectId: null,
    linkedAssetId: null,
    format: IMAGE_EDITOR_PROJECT_FORMAT,
    mimeType: IMAGE_EDITOR_PROJECT_MIME,
    editorDocumentKind: IMAGE_EDITOR_PROJECT_KIND,
    autosave,
    documentId: editorDoc.id,
    revision: editorDoc.revision,
    layerCount: editorDoc.layers.length,
    width: editorDoc.width,
    height: editorDoc.height,
    previewStoragePath: previewUpload.storagePath,
    downloadUrl: previewUpload.downloadUrl,
    bytes: previewUpload.bytes,
    storagePath: previewUpload.storagePath,
    editorDocumentManifest: manifest,
    editorLayerBlobs: layerUploads,
    updatedAt: serverTimestamp(),
    updatedByUid: uid,
  };
  if (prevSnap.exists()) {
    await setDoc(nodeRef, basePayload, { merge: true });
  } else {
    await setDoc(nodeRef, {
      ...basePayload,
      createdAt: serverTimestamp(),
      createdByUid: uid,
    });
  }
  return {
    nodeId,
    previewUrl: previewUpload.downloadUrl,
    manifest,
    autosave,
  };
}

/**
 * Load layered project node and reconstruct EditorDocument.
 *
 * @param {{ companyId: string, nodeId: string }} params
 */
export async function loadLayeredEditorProject(params) {
  const companyId = String(params.companyId || '').trim();
  const nodeId = String(params.nodeId || '').trim();
  if (!companyId || !nodeId) throw new Error('companyId and nodeId are required.');
  const snap = await getDoc(filesNodeRef(companyId, nodeId));
  if (!snap.exists()) throw new Error('Layered project not found.');
  const node = { id: snap.id, ...snap.data() };
  if (node.editorDocumentKind !== IMAGE_EDITOR_PROJECT_KIND) {
    throw new Error('Selected file is not a layered project.');
  }
  const manifest = node.editorDocumentManifest;
  if (!manifest || !Array.isArray(manifest.layers)) {
    throw new Error('Layered project manifest is missing.');
  }
  /** @type {Record<string, ImageData>} */
  const layerImageDataById = {};
  for (const layer of manifest.layers) {
    if (!layer?.id) {
      throw new Error('Layer id missing in project manifest.');
    }
    // v2 text layers: params live in the manifest; no PNG blob.
    if (
      layer.kind === 'text' &&
      layer.text &&
      typeof layer.text === 'object'
    ) {
      continue;
    }
    if (!layer?.storagePath) {
      throw new Error(`Layer storage path missing for ${layer.id}.`);
    }
    const url = await getDownloadURL(ref(storage, layer.storagePath));
    const imageData = await loadImageSrcToImageData(url);
    layerImageDataById[layer.id] = imageData;
  }
  const docData = rehydrateLayeredProjectManifest(manifest, layerImageDataById);
  docData.persistedRevision = Number(node.revision || manifest.revision || 0);
  return {
    node,
    document: docData,
  };
}

/**
 * List recent autosave layered projects for this user (default 7 days).
 * @param {{ companyId: string, uid: string, maxAgeDays?: number }} params
 */
export async function listRecentAutosaveLayeredProjects(params) {
  const companyId = String(params.companyId || '').trim();
  const uid = String(params.uid || '').trim();
  if (!companyId || !uid) return [];
  const { myImageEditorAutosaveFolderId } = await ensureMyImageEditorFolders(
    companyId,
    uid,
  );
  let snap;
  try {
    snap = await getDocs(
      query(
        collection(db, 'companies', companyId, 'files_nodes'),
        where('kind', '==', FILES_KIND_FILE),
        where('ownerScope', '==', FILES_OWNER_SCOPE_USER),
        where('ownerUid', '==', uid),
        where('parentId', '==', myImageEditorAutosaveFolderId),
      ),
    );
  } catch {
    // Best-effort fallback when local index state lags.
    snap = await getDocs(collection(db, 'companies', companyId, 'files_nodes'));
  }
  const cutoffMs = Date.now() - (Number(params.maxAgeDays || 7) * 24 * 60 * 60 * 1000);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((node) =>
      node.kind === FILES_KIND_FILE &&
      node.ownerScope === FILES_OWNER_SCOPE_USER &&
      node.ownerUid === uid &&
      node.parentId === myImageEditorAutosaveFolderId &&
      node.editorDocumentKind === IMAGE_EDITOR_PROJECT_KIND &&
      node.autosave === true,
    )
    .filter((node) => {
      const ms = node.updatedAt?.toMillis?.() || 0;
      return ms >= cutoffMs;
    })
    .filter((node) => {
      const previewPath =
        (typeof node.previewStoragePath === 'string' && node.previewStoragePath) ||
        (typeof node.storagePath === 'string' && node.storagePath) ||
        '';
      if (!previewPath) return false;
      return storagePathMatchesOwnerScope({
        companyId,
        ownerScope: FILES_OWNER_SCOPE_USER,
        ownerUid: uid,
        storagePath: previewPath,
      });
    })
    .sort((a, b) => (b.updatedAt?.toMillis?.() || 0) - (a.updatedAt?.toMillis?.() || 0));
}

/**
 * Recent documents for the Image Editor hub landing: autosaves (My Files)
 * plus explicit Files/Drafts saves, newest first.
 *
 * @param {{
 *   companyId: string,
 *   uid: string,
 *   maxAgeDays?: number,
 *   limit?: number,
 * }} params
 * @returns {Promise<Array<{
 *   id: string,
 *   name: string,
 *   imageUrl: string,
 *   updatedAt: unknown,
 *   updatedAtMs: number,
 *   layerCount: number|null,
 *   isLayered: boolean,
 *   isAutosave: boolean,
 *   editorDocumentKind: string|null,
 *   format: string|null,
 *   type: string,
 *   category: string,
 *   promptUsed: string,
 * }>>}
 */
export async function listRecentImageEditorDocuments(params) {
  const companyId = String(params.companyId || '').trim();
  const uid = String(params.uid || '').trim();
  if (!companyId || !uid) return [];
  const limit = Math.max(1, Math.min(40, Number(params.limit) || 16));
  const maxAgeDays = Number(params.maxAgeDays) || 7;

  const [autosaveRows, draftRows] = await Promise.all([
    listRecentAutosaveLayeredProjects({ companyId, uid, maxAgeDays }),
    listImageEditorDraftsForOpen({ companyId, uid }),
  ]);

  /** @type {Map<string, ReturnType<typeof toImageEditorDraftOpenItem> & { updatedAtMs: number, isLayered: boolean, isAutosave: boolean }>} */
  const byId = new Map();

  const pushItem = (raw, { forceAutosave = false } = {}) => {
    const item = toImageEditorDraftOpenItem(raw);
    if (!item.id) return;
    const updatedAtMs =
      item.updatedAt?.toMillis?.() ||
      (typeof item.updatedAt?.seconds === 'number'
        ? item.updatedAt.seconds * 1000
        : 0);
    const isLayered = item.editorDocumentKind === IMAGE_EDITOR_PROJECT_KIND;
    const isAutosave = forceAutosave || item.autosave === true;
    const ownerScope =
      raw.ownerScope === 'user' || raw.ownerScope === 'company'
        ? raw.ownerScope
        : item.ownerScope === 'user' || item.ownerScope === 'company'
          ? item.ownerScope
          : forceAutosave
            ? 'user'
            : 'company';
    const next = {
      ...item,
      name: item.assetName,
      updatedAtMs,
      isLayered,
      isAutosave,
      filesNode: {
        id: item.id,
        kind: raw.kind === 'folder' || item.kind === 'folder' ? 'folder' : 'file',
        ownerScope,
        ownerUid:
          typeof raw.ownerUid === 'string'
            ? raw.ownerUid
            : typeof item.ownerUid === 'string'
              ? item.ownerUid
              : forceAutosave
                ? uid
                : null,
        storagePath: raw.storagePath || item.storagePath || null,
        previewStoragePath:
          raw.previewStoragePath || item.previewStoragePath || null,
        editorLayerBlobs: Array.isArray(raw.editorLayerBlobs)
          ? raw.editorLayerBlobs
          : Array.isArray(item.editorLayerBlobs)
            ? item.editorLayerBlobs
            : null,
        editorDocumentManifest:
          (raw.editorDocumentManifest &&
            typeof raw.editorDocumentManifest === 'object' &&
            raw.editorDocumentManifest) ||
          (item.editorDocumentManifest &&
            typeof item.editorDocumentManifest === 'object' &&
            item.editorDocumentManifest) ||
          null,
      },
    };
    const prev = byId.get(item.id);
    if (!prev || next.updatedAtMs >= prev.updatedAtMs) {
      byId.set(item.id, next);
    }
  };

  for (const row of autosaveRows) {
    pushItem(row, { forceAutosave: true });
  }
  for (const row of draftRows) {
    pushItem(row);
  }

  return [...byId.values()]
    .sort((a, b) => b.updatedAtMs - a.updatedAtMs)
    .slice(0, limit)
    .map((item) => ({
      id: item.id,
      name: item.name || item.assetName || item.id,
      imageUrl: item.imageUrl || item.previewUrl || '',
      updatedAt: item.updatedAt,
      updatedAtMs: item.updatedAtMs,
      layerCount: item.layerCount,
      isLayered: item.isLayered,
      isAutosave: item.isAutosave,
      editorDocumentKind: item.editorDocumentKind,
      format: item.format,
      type: item.type || 'sprite',
      category: item.category || 'prop',
      promptUsed: item.promptUsed || '',
      // Full node snapshot for ACL-aware delete (Files deleteFilesNode).
      filesNode: item.filesNode || null,
    }));
}


